import { Box, Chip, Divider, Stack, type SxProps } from "@mui/material";
import { type Dispatch, type FC, type SetStateAction, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { INK, SURFACE } from "../../theme/tokens";
import type { NurseFullRole, NurseRole, OvertimeBadgeType, ShiftTime } from "../../utils/models";
import { OvertimeBadgeType as OTBadge } from "../../utils/models";
import { inverseRolesMap, rolesMap } from "../../utils/utilFunctions";
import { ContractSectionsDropdown } from "../team/ContractSectionsDropdown";
import { RoleOrJobSelector } from "../UIToolkit/molecules/RoleOrJobSelector";
import { DayViewLocationFilter } from "./DayViewLocationFitler";

interface IProps {
	onShiftTimeFilterChange?: (selectedShiftTimes: ShiftTime[]) => void;
	onRoleTypeFilterChange?: (roles: NurseFullRole[] | "All") => void;
	onJobTypeFilterChange?: (jobs: string[]) => void;
	shiftsTimeFilters: ShiftTime[];
	selectedShiftTimes: ShiftTime[];
	selectedRoleTypes: NurseFullRole[] | "All";
	selectedJobs: string[];
	selectedLocations: string[];
	setSelectedLocations: Dispatch<SetStateAction<string[]>>;
	availableLocations: string[];
	selectedContractSections?: Set<string>;
	onContractSectionsChange?: (sections: string[]) => void;
	selectedOtBadgeTypes?: OvertimeBadgeType[] | "All";
	onOtBadgeFilterChange?: (types: OvertimeBadgeType[] | "All") => void;
}

const commonChipSx = {
	fontSize: {
		xl: "13px",
		xs: "10px",
	},
	".MuiChip-label": {
		px: 1,
		py: 0.5,
	},
	height: "fit-content",
} as const satisfies SxProps;

const EMPTY_JOB_HANDLER = (_: string[]): void => {};

export const DayViewFilters: FC<IProps> = ({
	onShiftTimeFilterChange,
	onRoleTypeFilterChange,
	onJobTypeFilterChange,
	shiftsTimeFilters,
	selectedShiftTimes,
	selectedRoleTypes,
	selectedJobs,
	selectedLocations,
	setSelectedLocations,
	availableLocations,
	selectedContractSections,
	onContractSectionsChange,
	selectedOtBadgeTypes,
	onOtBadgeFilterChange,
}) => {
	const { t } = useTranslation();

	// RoleOrJobSelector emits/accepts short NurseRole + "all"; consumers here use NurseFullRole[] | "All".
	// "All" maps to ["all"] for the selector; an empty array (deselect) maps back to "All" for the consumer.
	const selectedRolesForSelector = useMemo<(NurseRole | "all")[]>(() => {
		if (selectedRoleTypes === "All") return ["all"];
		return selectedRoleTypes.map((fullRole) => inverseRolesMap[fullRole]).filter((r): r is NurseRole => Boolean(r));
	}, [selectedRoleTypes]);

	const handleSelectedRolesChanged = useCallback(
		(selected: (NurseRole | "all")[]) => {
			if (selected.length === 0 || selected.includes("all")) {
				onRoleTypeFilterChange?.("All");
				return;
			}
			onRoleTypeFilterChange?.(
				selected
					.filter((r) => r !== "all")
					.map((r) => rolesMap[r])
					.filter((r): r is NurseFullRole => Boolean(r)),
			);
		},
		[onRoleTypeFilterChange],
	);

	const handleShiftTimeClick = (shiftTime: ShiftTime) => {
		if (shiftTime === "All") {
			onShiftTimeFilterChange?.(["All"]);
			return;
		}

		const isCurrentlySelected = selectedShiftTimes.some((selectedTime) => isSelected(selectedTime, shiftTime));

		// Single-select: clicking the already selected filter reverts to "All", otherwise select only the clicked filter
		onShiftTimeFilterChange?.(isCurrentlySelected ? ["All"] : [shiftTime]);
	};

	const formatLabel = (shiftTime: ShiftTime): string => {
		if (typeof shiftTime === "string") return shiftTime;

		return `${shiftTime[0].toFormat("ha")}-${shiftTime[1].toFormat("ha")}`;
	};

	const isShiftTimeSelected = (shiftTime: ShiftTime): boolean => {
		return selectedShiftTimes.some((selectedTime) => isSelected(selectedTime, shiftTime));
	};

	const isSelected = useCallback((selectedTime: ShiftTime, shiftTime: ShiftTime): boolean => {
		if (typeof selectedTime === "string" && typeof shiftTime === "string") {
			return selectedTime === shiftTime;
		}
		if (Array.isArray(selectedTime) && Array.isArray(shiftTime)) {
			return selectedTime[0].equals(shiftTime[0]) && selectedTime[1].equals(shiftTime[1]);
		}
		return false;
	}, []);

	const handleOtBadgeClick = useCallback(
		(badgeType: OvertimeBadgeType) => {
			if (!selectedOtBadgeTypes || selectedOtBadgeTypes === "All") {
				onOtBadgeFilterChange?.([badgeType]);
				return;
			}

			const isCurrentlySelected = selectedOtBadgeTypes.includes(badgeType);
			// Single-select: clicking the already selected filter reverts to "All", otherwise select only the clicked filter
			onOtBadgeFilterChange?.(isCurrentlySelected ? "All" : [badgeType]);
		},
		[selectedOtBadgeTypes, onOtBadgeFilterChange],
	);

	const isOtBadgeSelected = useCallback(
		(badgeType: OvertimeBadgeType): boolean => {
			return !!selectedOtBadgeTypes && selectedOtBadgeTypes !== "All" && selectedOtBadgeTypes.includes(badgeType);
		},
		[selectedOtBadgeTypes],
	);

	return (
		<Box
			sx={{
				width: "100%",
				display: "flex",
				alignItems: "center",
				// The labelled Sort and Locations triggers made this row wider than a 1024px viewport, where
				// it used to fit: without wrapping, the selects push "Show Timeline" off the page.
				flexWrap: "wrap",
				px: 2,
				gap: 1,
			}}
		>
			<DayViewLocationFilter selectedLocations={selectedLocations} setSelectedLocations={setSelectedLocations} availableLocations={availableLocations} />
			<Stack
				direction="row"
				spacing={1}
				sx={{
					alignItems: "center",
					flexWrap: "wrap",
					rowGap: 0.5,
				}}
			>
				{shiftsTimeFilters.map((filter, index) => {
					const isSelected = isShiftTimeSelected(filter);
					return (
						<Chip
							key={index}
							sx={{
								fontWeight: 600,
								color: isSelected ? SURFACE : INK.SECONDARY,
								bgcolor: isSelected ? INK.SECONDARY : SURFACE,
								...commonChipSx,
							}}
							label={formatLabel(filter)}
							onClick={() => handleShiftTimeClick(filter)}
							variant={isSelected ? "filled" : "outlined"}
						/>
					);
				})}
			</Stack>
			{onOtBadgeFilterChange && (
				<>
					<Divider orientation="vertical" flexItem />
					<Stack
						direction="row"
						spacing={1}
						sx={{
							alignItems: "center",
							flexWrap: "wrap",
							rowGap: 0.5,
						}}
					>
						<Chip
							sx={{
								fontWeight: 600,
								color: isOtBadgeSelected(OTBadge.Weekly) ? SURFACE : INK.SECONDARY,
								bgcolor: isOtBadgeSelected(OTBadge.Weekly) ? INK.SECONDARY : SURFACE,
								...commonChipSx,
							}}
							label="Weekly OT"
							onClick={() => handleOtBadgeClick(OTBadge.Weekly)}
							variant={isOtBadgeSelected(OTBadge.Weekly) ? "filled" : "outlined"}
						/>
						<Chip
							sx={{
								fontWeight: 600,
								color: isOtBadgeSelected(OTBadge.Approaching) ? SURFACE : INK.SECONDARY,
								bgcolor: isOtBadgeSelected(OTBadge.Approaching) ? INK.SECONDARY : SURFACE,
								...commonChipSx,
							}}
							label="Approaching OT"
							onClick={() => handleOtBadgeClick(OTBadge.Approaching)}
							variant={isOtBadgeSelected(OTBadge.Approaching) ? "filled" : "outlined"}
						/>
					</Stack>
				</>
			)}
			<Divider orientation="vertical" flexItem />
			<RoleOrJobSelector
				selectedRoles={selectedRolesForSelector}
				onSelectedRolesChanged={handleSelectedRolesChanged}
				selectedJobIds={selectedJobs}
				onSelectedJobsChanged={onJobTypeFilterChange ?? EMPTY_JOB_HANDLER}
				label={t("job")}
				formControlSx={{ minWidth: "260px" }}
				selectSx={{ background: SURFACE }}
			/>
			{onContractSectionsChange && (
				<>
					<Divider orientation="vertical" flexItem />
					<ContractSectionsDropdown
						dataTestId="day-view-contract-sections-dropdown"
						selectedSections={selectedContractSections ?? []}
						onChange={onContractSectionsChange}
						overrideSelectSx={{ bgcolor: "white" }}
					/>
				</>
			)}
		</Box>
	);
};
